/**
 * Template T1 — "Hero + Gradient Caption"
 * Full-bleed hero image with bottom gradient and title/metadata overlay.
 */

import React from 'react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { getPriceCopy } from '@/lib/events/priceCopy';
import type { ShareCardProps } from './ShareCardBase';
import {
  FARMLY_COLORS,
  FARMLY_FONTS,
  GradientOverlay,
  FarmlyBadge,
  FallbackBg,
  HeroImage,
} from './ShareCardBase';

export function TemplateT1({ target, format: fmt, spec }: ShareCardProps) {
  const { w, h } = spec;
  const isStory = fmt === 'ig-story';
  const titleSize = isStory ? 56 : 48;
  const metaSize = isStory ? 24 : 22;
  const padding = 48;

  let dateLine = '';
  let priceLine = '';
  let supportingLine = '';
  const isShop = target.type === 'shop';
  const isProducer = target.type === 'producer';
  const usePlaceVariant = isShop || isProducer;

  if (target.type === 'activity') {
    if (target.timingMode === 'open' || target.timingMode === 'anytime') {
      dateLine = 'Öppet under säsong';
    } else if (target.startDate) {
      try {
        dateLine = format(new Date(target.startDate), "d MMMM yyyy · HH:mm", { locale: sv });
      } catch { /* ignore */ }
    } else if (target.timingMode === 'season') {
      dateLine = 'Säsongsaktivitet';
    }
    const price = getPriceCopy({ is_free: target.isFree, price_ore: target.priceOre });
    priceLine = price.primary ?? '';
  } else if (target.type === 'product') {
    priceLine = (target.priceOre / 100).toLocaleString('sv-SE') + ' kr';
    if (target.deliverySnippet) dateLine = target.deliverySnippet;
  } else if (target.type === 'shop') {
    supportingLine = target.shopIntro
      || 'Utvalda produkter från gården och närområdet';
    if (target.openingSnippet) dateLine = target.openingSnippet;
  } else if (target.type === 'producer') {
    supportingLine = target.tagline || 'Upptäck vår gård på Farmly';
    if (target.placeName) dateLine = target.placeName;
  }

  return (
    <div
      style={{
        position: 'relative',
        width: w,
        height: h,
        overflow: 'hidden',
        fontFamily: FARMLY_FONTS.body,
      }}
    >
      {/* Background */}
      {target.heroImageUrl ? (
        <HeroImage src={target.heroImageUrl} w={w} h={h} />
      ) : (
        <FallbackBg title={target.title} w={w} h={h} variant={usePlaceVariant ? 'place' : 'default'} />
      )}

      <GradientOverlay />

      {/* Content overlay */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 2,
          padding: padding,
          paddingBottom: isStory ? 120 : 80,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {/* Producer line */}
        <div
          style={{
            fontFamily: FARMLY_FONTS.body,
            fontSize: metaSize - 2,
            color: FARMLY_COLORS.warmSand,
            opacity: 0.85,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {target.producerName}
        </div>

        {/* Title */}
        <div
          style={{
            fontFamily: FARMLY_FONTS.display,
            fontSize: titleSize,
            lineHeight: 1.15,
            color: FARMLY_COLORS.white,
            maxWidth: w - padding * 2,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {target.title}
        </div>

        {/* Supporting line (shop intro / description) */}
        {supportingLine && (
          <div
            style={{
              fontFamily: FARMLY_FONTS.body,
              fontSize: metaSize - 2,
              lineHeight: 1.4,
              color: FARMLY_COLORS.warmSand,
              opacity: 0.85,
              maxWidth: w - padding * 2,
              marginTop: 4,
            }}
          >
            {supportingLine}
          </div>
        )}

        {/* Meta chips */}
        {(dateLine || priceLine) && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
            {dateLine && (
              <span
                style={{
                  fontSize: metaSize,
                  color: FARMLY_COLORS.warmSand,
                  opacity: 0.9,
                }}
              >
                {isShop ? '🕐' : isProducer ? '📍' : '📅'} {dateLine}
              </span>
            )}
            {priceLine && (
              <span
                style={{
                  fontSize: metaSize,
                  color: FARMLY_COLORS.warmSand,
                  opacity: 0.9,
                }}
              >
                💰 {priceLine}
              </span>
            )}
          </div>
        )}
      </div>

      <FarmlyBadge />
    </div>
  );
}
